/***************************************************************************************
 * @module       Redux 
 * @name         authSlice
 * @description  Authentication slice for managing user authentication state
 * @version      1.0.0
 * @license      Licensed under the MIT License
 * @createdon    May 2025
 * @since        1.0
 ***************************************************************************************/

  // authSlice.js

  import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
  import { UserInfoByToken } from '../../services/techus-services'; // Import your API service
  import ServiceUtils from '../../utils/techus-serviceUtils';

  const utilsService = ServiceUtils();

  export const fetchSession = createAsyncThunk('auth/fetchSession', async (_, { getState }) => {
    const token = localStorage.getItem('user_access_token');
    if (!token) {
      throw new Error('No token found');
    }

    try {
      const response = await UserInfoByToken({ token });
      // If interceptor hasn't run yet, response is a raw axios object — extract .data
      return response?.data !== undefined ? response.data : response;
    } catch (error) {
      throw error;
    }
  });

  const initialState = {
    token: localStorage.getItem('user_access_token') || null,
    user: null,
    status: 'idle',
    error: null,
  };

  const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
      login: (state, action) => {
        state.token = action.payload.token;
        state.user = action.payload?.user?.username;
        state.status = 'succeeded';
        localStorage.setItem('user_access_token', action.payload.token);
        utilsService.setSession('user', JSON.stringify(action.payload.user), true);
      },
      logout: (state) => {
        state.token = null;
        state.user = null;
        state.status = 'idle';
        localStorage.removeItem('user_access_token');
        localStorage.removeItem('WHPuser');
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchSession.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(fetchSession.fulfilled, (state, action) => {
          state.status = 'succeeded';
          state.user = action.payload?.user?.username;
          state.token = action.payload?.user?.token;
        })
        .addCase(fetchSession.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.error.message;
        });
    },
  });

  export const { login, logout } = authSlice.actions;
  export default authSlice.reducer;
